import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import IAlert from "../../types/Alert";
import ConfigUtils from "../../utils/Config";
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Collapse, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import ILog from "../../types/Log";
import LogService from "../../services/LogService";
import * as constantes from "../../common/Constantes";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import FacturarCitaDialog from "./facturarCita/FacturarCitaDialog";
import TablePagination from "../utilities/TablePagination";
import FacturacionService from "../../services/FacturacionService";
import { aplicarAnulacion, aplicarCambioEntidad } from "./common/filasAnulacion";
import { dentroDelPeriodo, fechaExpedicionFila, fechaListado, useFilasListado } from "./common/fechasListado";
import EstadoFacturacionIcon, { EstadoFacturacion, EstadoFacturacionToColorUnified, getEstadoFacturacion, etiquetaEstadoFacturacion } from "./common/EstadoFacturacionIcon";
import FilterListIcon from '@mui/icons-material/FilterList';
import { IFactura } from "./Facturas";
import ComplementariaFormDialog from "./facturarCita/ComplementariaFormDialog";
import PacienteService from "../../services/PacienteService";
import IPaciente from "../../types/Paciente";
import DetalleFacturaComplementaria from "./facturarCita/DetalleFacturaComplementaria";
import { formatCurrency, formatNumeroFactura, getFechaExpedicion } from "../../utils/Factura";
import SelectorEntidadConsulta from "./common/SelectorEntidadConsulta";

const locale = constantes.locale;

const FacturasParciales = () => {
    const history = useNavigate();
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
    // Una fila que se acaba de emitir toma la fecha de expedición de hoy (ver useFilasListado).
    const [registros, setRegistros] = useFilasListado();
    const [pacientes, setPacientes] = useState<IPaciente[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalFacturarCita, setModalFacturarCita] = useState(false);
    const [selectedServicio, setSelectedServicio] = useState<IFactura | null>(null);

    const [filterStart, setFilterStart] = useState<Date | null>(null);
    const [filterEnd, setFilterEnd] = useState<Date | null>(null);
    const [filterPaciente, setFilterPaciente] = useState<string | null>(null);
    const [filterEstado, setFilterEstado] = useState("");
    const [filterServicios, setFilterServicios] = useState<string[]>([]);
    const [filterComplementaria, setFilterComplementaria] = useState<boolean | null>(null);
    const [filterModalidad, setFilterModalidad] = useState("");
    const [filterEntidad, setFilterEntidad] = useState<string | null>(null); // null = principal, por defecto

    const [page, setPage] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    
    const [facturasRelacionadas, setFacturasRelacionadas] = useState<IFactura[]>([]);
    const [modalComplementaria, setModalComplementaria] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [modalDetalleComplementaria, setModalDetalleComplementaria] = useState(false);
    
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
      load();
    }, []);
    
    useEffect(() => {
      setPage(0);
    }, [filterStart, filterEnd, filterPaciente, filterEstado, filterServicios, filterComplementaria, filterEntidad]);

    const userId = ConfigUtils.getUserId();

    const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

    const load = async () => {
      try {
        const [pacientesResp, facturasParcialesResp] = await Promise.all([
          PacienteService.getAll(),
          FacturacionService.getFacturasParciales(userId),
        ]);
        setPacientes(pacientesResp.data || []);
        setRegistros(facturasParcialesResp.data || []);
      }  catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "FacturasParciales.Component.getFacturasParciales",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoading(false);
      }
    };

    if (loading) return <CircularProgress />;

    const filteredRegistros = registros.filter(r => {
      // Por fecha de expedición (la de emisión, o la de creación si no está emitida); la de pago es solo una columna.
      if (!dentroDelPeriodo(r, filterStart, filterEnd)) return false;
      
      if (filterPaciente && (!r.paciente || r.paciente.toLowerCase().indexOf(filterPaciente.toLowerCase()) === -1)) return false;
      if (filterEstado && filterEstado !== "" && r.estado !== filterEstado.toLowerCase()) return false;
      if (filterServicios && Array.isArray(filterServicios) && filterServicios.length > 0) {
        if (!r.servicio || !filterServicios.includes(r.servicio)) return false;
      }      if (filterModalidad && filterModalidad !== "" && r.modalidad !== filterModalidad.toLocaleLowerCase()) return false;
      if (filterComplementaria !== null) {
        if (r.modalidad === "complementaria") return false;
        const tieneComplementaria = !!r.facturaRelacionada;
        if (tieneComplementaria !== filterComplementaria) return false;
      }
      if ((r.entityId ?? null) !== filterEntidad) return false;
      return true;
    });

    const pacientesUnicos = Array.from(
      new Set(registros.map(r => r.paciente).filter(p => p !== null))
    ).sort((a, b) => a!.localeCompare(b!, "es", { sensitivity: "base" }));

    const estados = Array.from(
      new Set(registros.map(r => r.estado).filter(p => p !== null))
    ).map(p => p.charAt(0).toUpperCase() + p.slice(1));

    const servicios = Array.from(
      new Set(registros.map(r => r.servicio).filter(p => p !== null))
    ).sort((a, b) => a!.localeCompare(b!, "es", { sensitivity: "base" }));

    const modalidades = Array.from(
      new Set(registros.map(r => r.modalidad).filter(p => p !== null))
    ).map(p => p.charAt(0).toUpperCase() + p.slice(1));

    const registrosPaginados = filteredRegistros.slice(
      page * constantes.rowsPerPage,
      page * constantes.rowsPerPage + constantes.rowsPerPage
    );

    const parseEstadoFacturacion = (estadoKey?: string): EstadoFacturacion =>
        Object.values(EstadoFacturacion).includes(estadoKey as EstadoFacturacion)
          ? (estadoKey as EstadoFacturacion)
          : EstadoFacturacion.SIN_FACTURA;

    const inputStyle = {
      "& .MuiInputBase-root": {
        height: 40,
        minHeight: 40,
        fontSize: 13,
        "& .MuiInputBase-input": {
          padding: "8px 12px",
          height: 24,
          boxSizing: "border-box",
          fontSize: 13,
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: 13,
      },
      "& .MuiAutocomplete-inputRoot": {
        minHeight: 40,
      },
    };

    const selectableIds = filteredRegistros
      .filter(r =>
        r.tipo !== "agrupada" &&
        r.tipo !== "rectificativa" &&
        r.facturaRelacionada === null &&
        (r.estado === EstadoFacturacion.EMITIDA || r.estado === EstadoFacturacion.EMITIDA_PENDIENTE_PAGO)
      )
      .map(r => r._id);

    const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));

    const facturasSeleccionadas = registros.filter(r =>
      selectedIds.includes(r._id)
    );

    return(
        <>        
          {registros.length === 0 ? (
            <Typography variant="body1">
              No hay facturas parciales.
            </Typography>
          ) : (
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    flex: 1,
                    minWidth: 0,
                    fontSize: { xs: "1.25rem", sm: "1.75rem" },
                    whiteSpace: { xs: "nowrap", sm: "normal" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {isDesktop ? "Facturas parciales y complementarias" : "Parciales"}
                </Typography>

                {!isDesktop && (
                  <IconButton onClick={() => setShowFilters(prev => !prev)}>
                    <FilterListIcon />
                  </IconButton>
                )}
              </Box>
            
              {/* FILTROS */}
              <Collapse in={isDesktop || showFilters} timeout="auto" unmountOnExit>
              <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                  <SelectorEntidadConsulta value={filterEntidad} onChange={value => { setFilterEntidad(value); setSelectedIds([]); }} />
                  <DatePicker
                    label="Desde"
                    value={filterStart}
                    onChange={setFilterStart}
                    renderInput={(params) => (
                      <TextField {...params} sx={{ width: { xs: "100%", sm: 140 }, ...inputStyle }} size="small" InputLabelProps={{ style: { fontSize: 13 } }} />
                    )}
                  />
                  <DatePicker
                    label="Hasta"
                    value={filterEnd}
                    onChange={setFilterEnd}
                    renderInput={(params) => (
                      <TextField {...params} sx={{ width: { xs: "100%", sm: 140 }, ...inputStyle }} size="small" InputLabelProps={{ style: { fontSize: 13 } }} />
                    )}
                  />
                  <Autocomplete
                    disablePortal
                    options={pacientesUnicos}
                    value={filterPaciente}
                    onChange={(event, newValue) => setFilterPaciente(newValue)}
                    renderInput={(params) => <TextField {...params} label="Paciente" size="small" sx={{ ...inputStyle, minWidth: 80, width: "100%" }} InputLabelProps={{ style: { fontSize: 13 } }} />}
                    sx={{ flexGrow: 1, minWidth: 100 }}
                  />
                  <FormControl sx={{ width: { xs: "100%", sm: 140 } }} size="small">
                    <InputLabel sx={{ fontSize: 13 }}>Servicio</InputLabel>
                    <Select
                      multiple
                      value={filterServicios}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilterServicios(typeof value === "string" ? value.split(",") : value);
                      }}
                      label="Servicio"
                      renderValue={(selected) => (selected as string[]).join(", ")}
                      sx={{
                        "& .MuiSelect-select": {
                          fontSize: 13,
                        }
                      }}
                    >
                      {servicios.map(t => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ width: { xs: "100%", sm: 140 } }} size="small">
                    <InputLabel sx={{ fontSize: 13 }}>Modalidad</InputLabel>
                    <Select
                      value={filterModalidad}
                      onChange={(e) => setFilterModalidad(e.target.value)}
                      label="Modalidad"
                      sx={{
                        "& .MuiSelect-select": {
                          fontSize: 13,
                        }
                      }}
                    >
                      {modalidades.map(t => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ width: { xs: "100%", sm: 180 } }} size="small">
                      <InputLabel sx={{ fontSize: 13 }}>Complementaria</InputLabel>
                      <Select
                        value={
                          filterComplementaria === null
                            ? ""
                            : filterComplementaria
                            ? "con"
                            : "sin"
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "") setFilterComplementaria(null);
                          else setFilterComplementaria(value === "con");
                        }}
                        label="Complementaria"
                        sx={{
                          "& .MuiSelect-select": {
                            fontSize: 13,
                          }
                        }}
                      >
                        <MenuItem value="con">Con complementaria</MenuItem>
                        <MenuItem value="sin">Sin complementaria</MenuItem>
                      </Select>
                    </FormControl>
                  <FormControl sx={{ width: { xs: "100%", sm: 140 } }} size="small">
                    <InputLabel sx={{ fontSize: 13 }}>Estado</InputLabel>
                    <Select
                      value={filterEstado}
                      onChange={(e) => setFilterEstado(e.target.value)}
                      label="Estado"
                      sx={{
                        "& .MuiSelect-select": {
                          fontSize: 13,
                        }
                      }}
                    >
                      {estados.map(s => (
                        <MenuItem key={s} value={s}>{etiquetaEstadoFacturacion(s)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                    onClick={() => {
                      setFilterStart(null);
                      setFilterEnd(null);
                      setFilterPaciente(null);
                      setFilterEstado("");
                      setFilterServicios([]);
                      setFilterComplementaria(null);
                      setFilterModalidad("");
                      setFilterEntidad(null);
                      setSelectedIds([]);
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </Box>
              </LocalizationProvider>
              </Collapse>

              {isMobile ? <Box>
                {registrosPaginados.map((item) => {
                  const estado = parseEstadoFacturacion(item.estado);
                  const colores = EstadoFacturacionToColorUnified[estado];
                
                  return (
                    <Paper key={item._id} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
  
                      <Typography variant="body1" fontWeight={500}>
                        {item.paciente}
                      </Typography>

                      <Box mt={0.5}>
                        {item.estado === EstadoFacturacion.ANULADA ? (
                          <Chip
                            label={item.estado}
                            size="small"
                            sx={{ textTransform: "capitalize", border: `1px solid #d32f2f`, color: "#d32f2f" }}
                          />
                        ) : (
                          <Chip
                            label={etiquetaEstadoFacturacion(item.estado)}
                            color={colores.chip}
                            size="small"
                            sx={{ textTransform: "capitalize" }}
                          />
                        )}
                      </Box>

                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {[fechaListado(fechaExpedicionFila(item)), formatNumeroFactura(item.numero)].filter(Boolean).join(" · ")}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {[item.tipo, item.servicio, item.modalidad]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                        
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={1.5}
                      >
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(item.total, item.moneda)}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" gap={1}>
                          <EstadoFacturacionIcon
                            estado={estado}
                            onClick={() => {
                              setSelectedServicio(item);
                              if (item.tipo === "cita") setModalFacturarCita(true);
                              if (item.tipo === "agrupada") {
                                setFacturasRelacionadas(
                                  registros.filter(r => 
                                    r.facturaRelacionada?._id === item._id
                                  )
                                );
                                setModalDetalleComplementaria(true);
                              };
                            }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box> : 
              <TableContainer>
                {selectedIds.length > 0 && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={2}
                  py={1}
                  sx={{
                    backgroundColor: "grey.50",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {selectedIds.length} facturas seleccionadas
                  </Typography>
                
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setModalComplementaria(true)}
                    >
                      Crear complementaria
                    </Button>
                  </Box>
                </Box>
              )}
              <Table size="small">
                <TableHead 
                  sx={{ 
                    backgroundColor: "grey.100",
                    "& .MuiTableCell-head": {
                      fontWeight: 600,
                      color: "grey.800",
                      fontSize: "0.95rem",
                    }
                  }}
                >
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedIds.length > 0 && !allSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(selectableIds);
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: "20%" }}>Número</TableCell>
                    <TableCell sx={{ width: { xs: 110, sm: 130 }, whiteSpace: "nowrap" }}>Fecha de expedición</TableCell>
                    <TableCell sx={{ width: { xs: 110, sm: 130 }, whiteSpace: "nowrap" }}>Fecha de pago</TableCell>
                    <TableCell sx={{ width: "30%" }}>Paciente</TableCell>
                    <TableCell sx={{ width: "20%" }}>Servicio</TableCell>
                    <TableCell sx={{ width: "20%" }}>Modalidad</TableCell>
                    <TableCell sx={{ width: "20%" }}>Complementaria</TableCell>
                    <TableCell sx={{ width: "20%" }}>Estado</TableCell>
                    <TableCell sx={{ width: "20%" }}>Importe</TableCell>
                    <TableCell sx={{ width: 60 }} align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
              
                <TableBody>
                  {registrosPaginados.map((item, index) => {
                    const estado = parseEstadoFacturacion(item.estado);
                    const colores = EstadoFacturacionToColorUnified[estado];
                
                    return (
                      <TableRow 
                        key={item._id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "white" : "grey.50",
                          "&:hover": {
                            backgroundColor: "grey.100"
                          }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            disabled={!selectableIds.includes(item._id)}
                            checked={selectedIds.includes(item._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(prev => [...prev, item._id]);
                              } else {
                                setSelectedIds(prev => prev.filter(id => id !== item._id));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatNumeroFactura(item.numero)}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{fechaListado(fechaExpedicionFila(item))}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.fechaPago ? new Date(item.fechaPago).toLocaleDateString("es-ES") : ""}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{item.paciente}</TableCell>
                        <TableCell>{item.servicio}</TableCell>
                        <TableCell>{item.modalidad ? item.modalidad.charAt(0).toUpperCase() + item.modalidad.slice(1) : ''}</TableCell>
                        <TableCell>
                          {item.facturaRelacionada
                            ? formatNumeroFactura(item.facturaRelacionada.numero)
                            : item.facturaRelacionada === undefined && item.tipo === "agrupada"
                            ? (
                              <Typography variant="caption" color="text.secondary">
                                No aplica
                              </Typography>
                            )
                            : (
                              <Typography variant="caption" color="text.secondary">
                                Pendiente
                              </Typography>
                            )}
                        </TableCell>
                        <TableCell>
                          {item.estado === EstadoFacturacion.ANULADA ? (
                            <Chip
                              label={item.estado}
                              size="small"
                              sx={{ textTransform: "capitalize", border: `1px solid #d32f2f`, color: "#d32f2f" }}
                            />
                          ) : (
                            <Chip
                              label={etiquetaEstadoFacturacion(item.estado)}
                              color={colores.chip}
                              size="small"
                              sx={{ textTransform: "capitalize" }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatCurrency(item.total, item.moneda)}</TableCell>
                        <TableCell align="right">
                          <EstadoFacturacionIcon
                            estado={estado}
                            onClick={() => {
                              setSelectedServicio(item);
                              if (item.tipo === "cita") setModalFacturarCita(true);
                              if (item.tipo === "agrupada") {
                                setFacturasRelacionadas(
                                  registros.filter(r => 
                                    r.facturaRelacionada?._id === item._id
                                  )
                                );
                                setModalDetalleComplementaria(true);
                              };
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TableContainer>}
              
              {/* PAGINACIÓN */}
              <TablePagination
                count={filteredRegistros.length}
                page={page}
                rowsPerPage={constantes.rowsPerPage}
                onPageChange={setPage}
              />
            </Paper>
          )}
    
        {/* FACTURAR CITA */}
        {modalFacturarCita && 
            <FacturarCitaDialog 
                open={modalFacturarCita} 
                tipo="original"
                onClose={() => {setModalFacturarCita(false); setSelectedServicio(null)}} 
                citaId={selectedServicio?.modalidad === "rectificativa" ? selectedServicio._id : selectedServicio?.servicioId!}
                pacienteId={selectedServicio?.pacienteId!}
                onServicioActualizado={({ _id: servicioId, sinAviso, ...cambios }) => {
                  setRegistros(prev =>
                    prev.map(r => r.servicioId === servicioId ? { ...r, ...cambios } : r)
                  );
                  if (!sinAviso) setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
                }}
                onFacturaRectificada={(factura, pacienteId) => {
                  const facturaReducida: IFactura = {
                    _id: factura._id,
                    numero: factura.numero,
                    fechaFactura: factura.fechaFactura.toString(),
                    fechaExpedicion: getFechaExpedicion(factura),
                    tipo: factura.tipo,
                    modalidad:  "rectificativa",
                    servicio: factura.servicio,
                    pacienteId: pacienteId,
                    paciente: factura.receptor.nombre,
                    servicioId: "",
                    total: factura.importeTotal,
                    estado: getEstadoFacturacion(factura.estado, factura.estadoPago),
                    facturaRelacionada: undefined,
                    fechaPago: factura.metodoPago?.fechaPago.toString(),
                    entityId: factura.entityId ?? null,
                  };
                  setRegistros(() => [facturaReducida, ...registros]);
                  setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
                }}
                onRectificativaActualizada={(updated) => {
                  setRegistros(prev =>
                    prev.map(r => r._id === updated._id ? { ...r, ...updated } : r)
                  );
                  setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
                }}
                onAnularFactura={(id, _motivo, _devolverSesiones, anulacion) => {
                  setRegistros(prev => aplicarAnulacion(prev, id, anulacion));
                }}
                onEntidadCambiada={(facturaAnuladaId, facturaNueva, anulacion) => {
                  setRegistros(prev => aplicarCambioEntidad(prev, facturaAnuladaId, facturaNueva, anulacion));
                  setAlert({ open: true, severity: "success", msg: "Entidad de facturación cambiada correctamente" });
                }}
                onFacturaEliminada={(id) => {
                  setRegistros(prev => prev.filter(r => r._id !== id));
                  setAlert({ open: true, severity: "success", msg: "Factura eliminada correctamente" });
                }}
            />
        }

        {/* DETALLE FACTURA COMPLEMENTARIA */}
        {modalDetalleComplementaria && (
          <DetalleFacturaComplementaria
            open={modalDetalleComplementaria}
            onClose={() => {setModalDetalleComplementaria(false); setSelectedServicio(null)}}
            factura={selectedServicio!}
            facturasParciales={facturasRelacionadas}
            pacientes={pacientes}
            onFacturaPagada={(f) => {
              setSelectedServicio(f);
              setRegistros(prev =>
                prev.map(r =>
                  r._id === f?._id
                    ? {
                        ...r,
                        estado: f?.estado ?? r.estado,
                        fechaPago: f?.fechaPago ?? null,
                      }
                    : r
                )
              );
              setAlert({ open: true, severity: 'success', msg: 'Factura pagada correctamente' });
            }}
            onFacturaEmitida={(f) => {
              setSelectedServicio(f);
              setRegistros(prev =>
                prev.map(r =>
                  r._id === f?._id
                    ? {
                        ...r,
                        estado: f?.estado ?? r.estado,
                        numero: f?.numero ?? r.numero,
                      }
                    : r.facturaRelacionada?._id === f?._id
                      ? {
                          ...r,
                          facturaRelacionada: {
                            ...r.facturaRelacionada,
                            estado: f?.estado ?? r.facturaRelacionada.estado,
                            numero: f?.numero ?? r.facturaRelacionada.numero,
                          },
                        }
                      : r
                )
              );
              setAlert({ open: true, severity: 'success', msg: 'Factura emitida correctamente' });
            }}
            onFacturaActualizada={({ facturaComplementaria, parcialesAsociadas, parcialesDesasociadas }) => {
              const facturaReducida: IFactura = {
                _id: facturaComplementaria._id,
                numero: facturaComplementaria.numero,
                fechaFactura: facturaComplementaria.fechaFactura.toString(),
                fechaExpedicion: getFechaExpedicion(facturaComplementaria),
                tipo: facturaComplementaria.tipo,
                modalidad: "complementaria",
                servicio: facturaComplementaria.servicio,
                pacienteId: null,
                paciente: facturaComplementaria.receptor.nombre,
                servicioId: "",
                total: facturaComplementaria.importeTotal,
                estado: getEstadoFacturacion(facturaComplementaria.estado, facturaComplementaria.estadoPago),
                facturaRelacionada: undefined,
                fechaPago: facturaComplementaria.metodoPago?.fechaPago.toString(),
                entityId: facturaComplementaria.entityId ?? null,
              };
              setRegistros(prev =>
                prev.map(r => {
                  if (r._id === facturaReducida._id) {
                    return facturaReducida;
                  }
                  if (parcialesAsociadas && r.numero && parcialesAsociadas.includes(r.numero)) {
                    return {
                      ...r,
                      facturaRelacionada: facturaReducida,
                    };
                  }
                  if (parcialesDesasociadas && r.numero && parcialesDesasociadas.includes(r.numero)) {
                    return {
                      ...r,
                      facturaRelacionada: undefined,
                    };
                  }
                  return r;
                })
              );
              setAlert({ open: true, severity: 'success', msg: 'Factura actualizada correctamente' });
            }}
            onFacturaRectificada={({ factura }) => {
              setRegistros(prev => [factura, ...prev]);
              setAlert({ open: true, severity: 'success', msg: 'Factura rectificada correctamente' });
            }}
          />
        )}
        {/* MODAL CREAR COMPLEMENTARIA */}
        {modalComplementaria && (
          <ComplementariaFormDialog
            open={modalComplementaria}
            onClose={() => setModalComplementaria(false)}
            facturasParciales={facturasSeleccionadas}
            pacientes={pacientes}
            onFacturaCreada={({
              facturaComplementaria,
              paciente,
              tipoFacturacion
            }) => {
              const facturaReducida: IFactura = {
                _id: facturaComplementaria._id,
                numero: facturaComplementaria.numero,
                fechaFactura: facturaComplementaria.fechaFactura.toString(),
                fechaExpedicion: getFechaExpedicion(facturaComplementaria),
                tipo: facturaComplementaria.tipo,
                modalidad: tipoFacturacion === "complementaria" ? "complementaria" : "parcial",
                servicio: facturaComplementaria.servicio,
                pacienteId: paciente?._id || null,
                paciente: facturaComplementaria.receptor.nombre,
                servicioId: "",
                total: facturaComplementaria.importeTotal,
                estado: getEstadoFacturacion(facturaComplementaria.estado, facturaComplementaria.estadoPago),
                facturaRelacionada: undefined,
                fechaPago: facturaComplementaria.metodoPago?.fechaPago.toString(),
                entityId: facturaComplementaria.entityId ?? null,
              };
              const nuevosRegistros = registros.map(r => {
                if (selectedIds.includes(r._id)) {
                  return { ...r, facturaRelacionada: facturaReducida };
                }
                return r;
              });
              setRegistros(() => [facturaReducida, ...nuevosRegistros]);
              setSelectedServicio(facturaReducida);
              setFacturasRelacionadas(facturasSeleccionadas);
              setModalDetalleComplementaria(true);
              setSelectedIds([]);
              setAlert({ open: true, severity: "success", msg: "Factura creada correctamente" });
            }}
          />
        )}
    
        <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
          <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.msg}
          </Alert>
        </Snackbar>
    </>
    );
};

export default FacturasParciales;